'use client';

import React from 'react';
import { X, Languages } from 'lucide-react';
import { useLocale, type Locale } from '@/app/user-and-session/languages/i18n';
import SUPPORTED_LANGUAGES from '../languages';
import { useTranslations } from 'next-intl';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';

interface LanguageSelectPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LanguageSelectPopup({ isOpen, onClose }: LanguageSelectPopupProps) {
  const { locale, setLocale } = useLocale();
  const t = useTranslations('popup.languageSelect');
  const { userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, userActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );

  if (!isOpen) return null;

  const handleLanguageSelect = async (code: string) => {
    if (SUPPORTED_LANGUAGES[code]) {
      // Update locale provider
      setLocale(code as Locale);
      
      // If userAppData exists and is initialized, also update database
      if (userEmail && isAuthenticated && !authLoading) {
        try {
          await userActions.setLanguageAndWriteToDB(code);
        } catch (error) {
          console.error('Failed to save language preference to database:', error);
          // Continue anyway - locale is already updated
        }
      }
      
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div className="relative w-full max-w-md bg-[#1a1a1e] rounded-xl border border-[#ffffff1a] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#ffffff1a]">
          <div className="flex items-center gap-3">
            <Languages className="w-5 h-5 text-white" />
            <h3 className="text-white font-semibold text-lg font-['Montserrat',Helvetica]">
              {t('selectLanguage')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 text-gray-400 hover:text-white hover:bg-[#ffffff1a] rounded-lg transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Language List */}
        <div className="max-h-[60vh] overflow-y-auto dark-scrollbar">
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
            <button
              key={code}
              onClick={() => handleLanguageSelect(code)}
              className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#2a2a2e] transition-colors border-b border-gray-700 last:border-b-0 ${
                locale === code ? 'bg-[#2a2a2e]' : ''
              }`}
            >
              <span className="text-white text-base font-medium font-['Montserrat',Helvetica]">
                {name}
              </span>
              {locale === code && (
                <svg 
                  className="w-5 h-5 text-[#5856d6]" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

